/**
 * ProgrammingDetails component is used to display and manage various aspects of a programming activity within a course.
 * It includes functionalities such as managing submissions, testing submissions, displaying problem statements, and showing autograding configurations.
 * Split component is used to divide the screen into two parts, with the left part displaying the submission management 
 * and testing components, and the right part displaying the problem statement, test cases, and autograding configuration components.
 * The component uses several child components:
 * - ManageSubmission: For managing the submission of code
 * - TestSubmission: For testing the submitted code
 * - SubjectiveGrades: For managing subjective grading
 * - ProblemStatement: For displaying the problem statement of the activity
 * - DisplayTestCases: For displaying test cases related to the activity
 * - AutogradingConfiguration: For displaying and configuring autograding settings
 *
 * The component also handles reading additional files provided by the instructor and displays them if available.
 */

import React, { useState, useEffect, useRef } from "react";
import ProblemStatement from "../ProblemStatement/ProblemStatement.jsx";
import ManageSubmission from "../ManageSubmissions/ManageSubmissions.jsx";
import TestSubmission from "../TestSubmission/TestSubmission.jsx";
import DisplayTestCases from "../DisplayTestCases/DisplayTestCases.jsx";
import AutogradingConfiguration from "../AutogradingConfiguration/AutogradingConfiguration.jsx";
import SubjectiveGrades from "../SubjectiveGrades/SubjectiveGrades.jsx";
import Split from '@uiw/react-split';
import Alert from "react-bootstrap/Alert";
import InstructorUploadedCode from "../InstructorUploadedCode/InstructorUploadedCode.jsx"

export default function ProgrammingDetails({
  programId,
  changeAccess,
  details,
  access,
  activity_title,
  activity_description,
  delegate_student_id,
  provideAdditionalFiles,
  instructorAdditionalFiles,
  document,
  displayRunWithoutSubmitButton,
}) {
  //--- useRef variables to invoke clicking action programmatically of buttons inside testcase results accordion when corresponding buttons in editor footer are clicked ---//
  const practiceMarks = useRef(null);
  const evaluateMarks = useRef(null);

  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [show, setShow] = useState(true);

  //usestate variable to store initial instructor uploaded code object in order to reset the code inside editor to initial driver code on clicking reset button
  const [initialCode,setInitialCode] = useState({});

  //usestate variable to store instructor uploaded code in string format
  const [codeString, setCodeString] = useState("");
  const [fileName, setFileName] = useState("");

  //boolean usestate variable for template coding activity
  const [isTemplateCode, setIsTemplateCode] = useState(false);

  //usestate variables for submission limits activty config
  const [showAttemptsLeft, setShowAttemptsLeft] = useState(true); 
  const [attemptsLeft, setAttemptsLeft] = useState({
    submission_attempts_left: 100,
    run_without_submit_attempts_left: 100
  });
  
  const [visibleTestcaseEvaluationResultsStatus, setVisibleTestcaseEvaluationResultsStatus] = useState(null);
  const [hiddenTestcaseEvaluationResultStatus, setHiddenTestcaseEvaluationResultStatus] = useState(null);

  //State to store the content of the additional files provided by the instructor
  const [
    instructorAdditionalFilesContent,
    setInstructorAdditionalFilesContent,
  ] = useState({});
 
  useEffect(()=>{
    const fetchCode = async () => {
      await objectToString(initialCode);
    };
    fetchCode();
  },[initialCode, instructorAdditionalFiles])

  //This useEffect is used to read the content of the additional files provided by the instructor
  useEffect(() => {
    const readFiles = async () => {
      const newFileContents = {};
      //Iterating over the languages for which the instructor has provided additional files
      if (
        instructorAdditionalFiles
      ) {
        for (const [language, files] of Object.entries(instructorAdditionalFiles)) {
          newFileContents[language] = {};

          //Iterating over the files provided by the instructor for the current language
          for (const file of files) {
            //Reading the content of the file using the readFile function
            const content = await readFile(file);
            newFileContents[language][file.name] = content;
          }
        }
      }
      //Setting the content of the additional files in the state
      setInstructorAdditionalFilesContent(newFileContents);
    };

    readFiles();
  }, [instructorAdditionalFiles]);

  //Function to read the content of a file
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      //Creating a new FileReader object
      const reader = new FileReader();
      //Setting the onload and onerror event handlers
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      //Reading the content of the file as text
      reader.readAsText(file);
    });
  };

  const renderAdditionalFilesAlert = () => {
    //Filtering the languages for which the instructor has provided additional files
    const languagesWithFiles = Object.keys(instructorAdditionalFiles).filter(
      (language) => instructorAdditionalFiles[language].length > 0
    );

    if (languagesWithFiles.length === 0) {
      return null;
    }
    //Rendering an alert to inform the student about the additional files provided by the instructor
    return (
      <Alert variant="info">
        The instructor has provided <b>additional files</b> for this activity.
        These files can be downloaded by clicking on them. Use them for Option
        1, but remember to submit only the file you have written. Additional
        files for:
        <ul>
          {languagesWithFiles.map((language) => (
            <li key={language}>
              {language}
              {" : "}
              {instructorAdditionalFiles[language].map((file, index) => (
                <a
                  key={index}
                  href={URL.createObjectURL(file)}
                  download={file.name}
                  style={{ marginRight: "8px" }}
                >
                  <u>{file.name}</u>
                  {""}
                </a>
              ))}
            </li>
          ))}
        </ul>
        The integrated editor will also display the additional files, but they
        will be read-only.
      </Alert>
    );
  };

  const objectToString = async (initialCode) => {
    let code = "";
    
    Object.entries(initialCode)?.map(([filename, content])=>{
      const files = fileName.split(',');
      // files?.forEach((file)=>{
      //   if(file.includes(filename)){
      //     log.log("This is the filename",file);
      //     filename=file;
      //   }
      // })
      code+= `File: ${filename}\n${content}\n\n`;
      setCodeString(code);
    })
    Object.keys(instructorAdditionalFiles)?.map((language)=>{
      if(instructorAdditionalFiles[language].length > 0 ){
        for(let i=0;i<instructorAdditionalFiles[language].length;i++){
          const file = instructorAdditionalFiles[language][i];
          const reader = new FileReader();
          
          reader.onload = function (event) {
            code += `File: ${file.name}\n${event.target.result}\n\n`
            setCodeString(code);
          };
          reader.onerror = function (event) {
              //log.error(`Error reading ${file.name}:, ${event.target.error}`);
          };
          reader.readAsText(file); 
          }
      }
    })
    return code;
  }   

  return (
    <>
      <div>
        {show && (
          <Alert variant="primary" onClose={() => setShow(false)} dismissible>
            Please either upload your code file using the <b>'Choose File'</b>{" "}
            button and using the <b>'Upload'</b> button or write your code in
            the editor below and click <b>'Submit Code'</b>. You only need to do
            one of these actions to submit your solution.
          </Alert>
        )}
        </div>
        <div>
        {showAttemptsLeft && (
          <Alert
            variant={
              [0, 1, 2, 3].includes(attemptsLeft.submission_attempts_left) ||
              [0, 1, 2, 3].includes(attemptsLeft.run_without_submit_attempts_left)
                ? "danger"
                : "info"
            }
            onClose={() => setShowAttemptsLeft(false)}
            dismissible
          >
            <ul style={{ marginBottom: 0 }}>
              <li>
                You have <b>{attemptsLeft.submission_attempts_left ?? '100'}</b> Submission attempt(s) left.
              </li>
              <li>
                You have <b>{attemptsLeft.run_without_submit_attempts_left ?? '100'}</b> Run without submit attempt(s) left.
              </li>
            </ul>
          </Alert>
        )}
      </div>
      <div>{provideAdditionalFiles && renderAdditionalFilesAlert()}</div>
      <Split>
        <div style={{ width: "62%", minWidth: "40%" }}>
          <div>
            {access?.enable_manage_submission ? (
              <ManageSubmission
                programId={programId}
                changeAccess={changeAccess}
                access={access}
                submission={details}
                setSelectedSubmission={setSelectedSubmission}
                setTestResults={setTestResults}
                instructorAdditionalFilesContent={
                  instructorAdditionalFilesContent
                }
                delegate_student_id={delegate_student_id}
                activityTitle={activity_title}
                initialCode={initialCode}
                setInitialCode={setInitialCode}
                setFileName = {setFileName}
                practiceMarks = {practiceMarks}
                evaluateMarks = {evaluateMarks}
                isTemplateCode={isTemplateCode}
                setIsTemplateCode={setIsTemplateCode}
                setVisibleTestcaseEvaluationResultsStatus={setVisibleTestcaseEvaluationResultsStatus}
                setHiddenTestcaseEvaluationResultStatus={setHiddenTestcaseEvaluationResultStatus}
                setAttemptsLeft={setAttemptsLeft}
                attemptsLeft={attemptsLeft}
                displayRunWithoutSubmitButton={displayRunWithoutSubmitButton}
              />
            ) : null}
          </div>
          
          <div>
            {delegate_student_id ? null : (
              <TestSubmission
                programId={programId}
                selectedSubmission={selectedSubmission}
                changeAccess={changeAccess}
                access={access}
                setTestResults={setTestResults}
                testResults={testResults}
                delegate_student_id={delegate_student_id}
                practiceMarks = {practiceMarks}
                evaluateMarks = {evaluateMarks}
                visibleTestcaseEvaluationResultsStatus={visibleTestcaseEvaluationResultsStatus}
                setVisibleTestcaseEvaluationResultsStatus={setVisibleTestcaseEvaluationResultsStatus}
                hiddenTestcaseEvaluationResultStatus={hiddenTestcaseEvaluationResultStatus}
                setHiddenTestcaseEvaluationResultStatus={setHiddenTestcaseEvaluationResultStatus}
              />
            )}
          </div>
          <div>
            {delegate_student_id ? null :
              <SubjectiveGrades
                programId={programId}
                selectedSubmission={selectedSubmission}
                delegate_student_id={delegate_student_id}
              />
            }
          </div>
        </div>
        <div
          style={{
            minWidth: "20%",
            flex: 1,
            maxHeight: "100%",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {access?.enable_display_problem_statement ? (
            <ProblemStatement
              activity_description={activity_description}
              activity_title={activity_title}
              additionalDocument={document}
            />
          ) : null}

          <DisplayTestCases programId={programId} testcasesData={details} />

          <AutogradingConfiguration
            isCaseSensitive={details?.case_sensitive}
            isIgnoreWhiteSpace={details?.ignore_whitespaces}
            isIndentationPercentage={details?.indentation_percentage}
            iscompareOnlyNumber={details?.compare_only_numbers}
            allow_delimeters={details?.allow_delimiters}
            errorRate={details?.error_rate}
            isOrderImportant={details?.order_is_important}
            linkerFlags={details?.linker_flags}
          />
          {/* instructor uploaded code accordion */}
          {isTemplateCode &&
            <InstructorUploadedCode code={codeString}/>
          }
        </div>
      </Split>
    </>
  );
}

ProgrammingDetails.defaultProps = {
  programId: "",
  changeAccess: () => { },
  details: {},
  access: {},
  activity_title: "",
  activity_description: "",
  delegate_student_id: null,
};
